import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
});

// ── Request interceptor: attach token ────────────────────────
api.interceptors.request.use(config => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor: refresh token on 401 ───────────────
let refreshing = false;
let queue = [];

api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      if (!refreshing) {
        refreshing = true;
        try {
          const rt = localStorage.getItem('refreshToken');
          const { data } = await axios.post('/api/auth/refresh', { refreshToken: rt });
          localStorage.setItem('accessToken', data.accessToken);
          queue.forEach(cb => cb(data.accessToken));
          queue = [];
        } catch {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        } finally {
          refreshing = false;
        }
      }
      return new Promise(resolve => {
        queue.push(token => {
          original.headers.Authorization = `Bearer ${token}`;
          resolve(api(original));
        });
      });
    }
    return Promise.reject(err);
  }
);

// ── Auth ─────────────────────────────────────────────────────
export const authAPI = {
  login:   (d) => api.post('/auth/login', d),
  refresh: (d) => api.post('/auth/refresh', d),
  logout:  (d) => api.post('/auth/logout', d),
  me:      ()  => api.get('/auth/me'),
};

// ── Users ────────────────────────────────────────────────────
export const usersAPI = {
  list:   ()     => api.get('/users'),
  create: (d)    => api.post('/users', d),
  update: (id,d) => api.put(`/users/${id}`, d),
  remove: (id)   => api.delete(`/users/${id}`),
};

// ── Groups ───────────────────────────────────────────────────
export const groupsAPI = {
  list:         ()        => api.get('/groups'),
  get:          (id)      => api.get(`/groups/${id}`),
  create:       (d)       => api.post('/groups', d),
  update:       (id,d)    => api.put(`/groups/${id}`, d),
  addMember:    (id,d)    => api.post(`/groups/${id}/members`, d),
  removeMember: (id,uid)  => api.delete(`/groups/${id}/members/${uid}`),
};

// ── Messages ─────────────────────────────────────────────────
export const messagesAPI = {
  get:         (gid, params) => api.get(`/messages/${gid}`, { params }),
  send:        (d)           => api.post('/messages', d),
  delete:      (id)          => api.delete(`/messages/${id}`),
  unreadCount: ()            => api.get('/messages/unread-count'),
};

// ── Files ────────────────────────────────────────────────────
export const filesAPI = {
  upload:   (fd, onProgress) => api.post('/files/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: p => onProgress && onProgress(Math.round(p.loaded*100/p.total)),
  }),
  list:     (params) => api.get('/files', { params }),
  download: (id)     => api.get(`/files/${id}/download`, { responseType: 'blob' }),
  archive:  (id)     => api.patch(`/files/${id}/archive`),
  remove:   (id)     => api.delete(`/files/${id}`),
};

// ── Meetings ─────────────────────────────────────────────────
export const meetingsAPI = {
  list:  ()     => api.get('/meetings'),
  create:(d)    => api.post('/meetings', d),
  start: (id)   => api.patch(`/meetings/${id}/start`),
  end:   (id)   => api.patch(`/meetings/${id}/end`),
};

// ── Circulars ────────────────────────────────────────────────
export const circularsAPI = {
  list:    ()    => api.get('/circulars'),
  create:  (d)   => api.post('/circulars', d),
  approve: (id)  => api.patch(`/circulars/${id}/approve`),
  send:    (id)  => api.patch(`/circulars/${id}/send`),
  markRead:(id)  => api.post(`/circulars/${id}/read`),
};

// ── Decisions ────────────────────────────────────────────────
export const decisionsAPI = {
  list:   ()      => api.get('/decisions'),
  create: (d)     => api.post('/decisions', d),
  sign:   (id, d) => api.post(`/decisions/${id}/sign`, d),
};

// ── Calendar ─────────────────────────────────────────────────
export const calendarAPI = {
  list:   (params) => api.get('/calendar', { params }),
  create: (d)      => api.post('/calendar', d),
};

// ── Notifications ────────────────────────────────────────────
export const notificationsAPI = {
  list:    ()    => api.get('/notifications'),
  markRead:(id)  => api.patch(`/notifications/${id}/read`),
  readAll: ()    => api.patch('/notifications/read-all'),
};

// ── KPI ──────────────────────────────────────────────────────
export const kpiAPI = { get: () => api.get('/kpi') };

// ── Logs ─────────────────────────────────────────────────────
export const logsAPI = { list: () => api.get('/logs') };

// ── AI ───────────────────────────────────────────────────────
export const aiAPI = {
  chat:             (d)    => api.post('/ai/chat', d),
  conversations:    ()     => api.get('/ai/conversations'),
  history:          (id)   => api.get(`/ai/conversations/${id}`),
  summarizeMeeting: (d)    => api.post('/ai/summarize-meeting', d),
};

export default api;
